import React, { useState } from 'react'
import HapticControls from './HapticControls'
import SubscriptionPage from '../UI/SubscriptionPage'
import { useSubscription } from '../../hooks/useSubscription'
import './ControlPanel.css'

const ControlPanel = ({
  isVisible,
  isRunning,
  frequency,
  verticalPosition,
  backgroundColor,
  ballColor,
  ballShape,
  ballSize,
  volume,
  soundType,
  soundFrequency,
  isFullscreen,
  user,
  shortcuts,
  onStart,
  onPause,
  onReset,
  onFrequencyChange,
  onVerticalPositionChange,
  onBackgroundColorChange,
  onBallColorChange,
  onBallShapeChange,
  onBallSizeChange,
  onVolumeChange,
  onSoundTypeChange,
  onSoundFrequencyChange,
  onToggleFullscreen,
  onSignOut,
  // Props Joy-Con
  joyConState,
  onJoyConConnect,
  onJoyConEnabledChange,
  onJoyConIntensityChange,
  // Props Gamepad
  gamepadState,
  onGamepadEnabledChange,
  onGamepadIntensityChange,
  onGamepadAssignSide,
  onGamepadScan
  }) => {
  const [showSubscriptionPage, setShowSubscriptionPage] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    visual: true,
    audio: false,
    shortcuts: false
  })
  const { 
    subscriptionStatus, 
    hasAccess, 
    statusMessage 
  } = useSubscription()

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  // Liste des formes disponibles avec emojis pour enfants
  const shapes = [
    { value: 'circle', label: '⚪ Cercle', emoji: null },
    { value: '🐶', label: '🐶 Chien' },
    { value: '🐱', label: '🐱 Chat' },
    { value: '🐻', label: '🐻 Ours' },
    { value: '🐰', label: '🐰 Lapin' },
    { value: '🦊', label: '🦊 Renard' },
    { value: '🐼', label: '🐼 Panda' },
    { value: '🐨', label: '🐨 Koala' },
    { value: '🦁', label: '🦁 Lion' },
    { value: '🐯', label: '🐯 Tigre' },
    { value: '🐸', label: '🐸 Grenouille' },
    { value: '🦄', label: '🦄 Licorne' },
    { value: '🦋', label: '🦋 Papillon' },
    { value: '🐝', label: '🐝 Abeille' },
    { value: '🐙', label: '🐙 Poulpe' },
    { value: '🦀', label: '🦀 Crabe' },
    { value: '🐢', label: '🐢 Tortue' },
    { value: '🦖', label: '🦖 Dinosaure' },
    { value: '🚀', label: '🚀 Fusée' },
    { value: '⭐', label: '⭐ Étoile' },
    { value: '🌟', label: '🌟 Étoile brillante' },
    { value: '💫', label: '💫 Étoiles filantes' },
    { value: '🎈', label: '🎈 Ballon' },
    { value: '🎯', label: '🎯 Cible' },
    { value: '⚽', label: '⚽ Ballon de foot' },
    { value: '🏀', label: '🏀 Basket' },
    { value: '🎨', label: '🎨 Palette' },
    { value: '🌈', label: '🌈 Arc-en-ciel' },
    { value: '🦸', label: '🦸 Super-héros' },
    { value: '🧙', label: '🧙 Magicien' },
    { value: '👑', label: '👑 Couronne' },
    { value: '💎', label: '💎 Diamant' },
    { value: '🍎', label: '🍎 Pomme' },
    { value: '🍓', label: '🍓 Fraise' },
    { value: '🍉', label: '🍉 Past\u00e8que' },
    { value: '🍕', label: '🍕 Pizza' },
    { value: '🍦', label: '🍦 Glace' },
    { value: '🍩', label: '🍩 Donut' },
    { value: '🎂', label: '🎂 G\u00e2teau' },
    { value: '🌺', label: '🌺 Fleur' },
    { value: '🌻', label: '🌻 Tournesol' },
    { value: '🌸', label: '🌸 Fleur de cerisier' },
  ]

  const formatFrequency = (freq) => {
    const period = 1000 / freq
    return `${freq} Hz (${Math.round(period)} ms)`
  }

  const handleSubscriptionClick = () => {
    setShowSubscriptionPage(true)
  }

  const getSubscriptionButton = () => {
    if (!subscriptionStatus) return null

    switch (subscriptionStatus.user_status) {
      case 'trial':
        return (
          <button 
            onClick={handleSubscriptionClick}
            className="subscription-btn trial"
            title="S'abonner"
          >
            🚀 S'abonner
          </button>
        )
      case 'expired':
        return (
          <button 
            onClick={handleSubscriptionClick}
            className="subscription-btn expired"
            title="Renouveler votre abonnement"
          >
            💳 S'abonner
          </button>
        )
      case 'subscribed':
        return (
          <span className="subscription-status active">
            ✅ Premium
          </span>
        )
      default:
        return null
    }
  }

  if (!isVisible) return null

  return (
    <>
      <div className="control-panel">
        {/* Bouton utilisateur en haut à droite */}
        {user && (
          <div className="user-menu">
            <button className="user-btn" onClick={onSignOut} title={user?.email || 'Utilisateur'}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}

      <h1>Application EMDR</h1>

      {/* Boutons de contrôle avec icônes */}
      <div className="buttons">
        <button 
          className="control-btn start-btn" 
          onClick={onStart}
          disabled={isRunning}
          title="Démarrer (Espace)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 5v14l11-7z" fill="currentColor"/>
          </svg>
          Démarrer
        </button>
        <button 
          className="control-btn pause-btn" 
          onClick={onPause}
          disabled={!isRunning}
          title="Pause (Espace)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor"/>
          </svg>
          Pause
        </button>
        <button 
          className="control-btn reset-btn" 
          onClick={onReset}
          title="Reset (R)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 3v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 21v-5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Reset
        </button>
        <button 
          className="control-btn fullscreen-btn" 
          onClick={onToggleFullscreen}
          title="Plein écran (F)"
        >
          {isFullscreen ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          {isFullscreen ? 'Fenêtré' : 'Plein écran'}
        </button>
      </div>

      {/* Section Mouvement */}
      <div className="param-box param-box-movement">
        <h3 className="box-title">🎯 Mouvement</h3>
        
        <div className="control-group compact">
          <label htmlFor="frequency">Fréquence</label>
          <input
            type="range"
            id="frequency"
            min="0.2"
            max="2.0"
            step="0.1"
            value={frequency}
            onChange={(e) => onFrequencyChange(parseFloat(e.target.value))}
          />
          <span className="control-value">{formatFrequency(frequency)}</span>
        </div>

        <div className="control-group compact">
          <label htmlFor="vertical-position">Position verticale</label>
          <input
            type="range"
            id="vertical-position"
            min="-400"
            max="400"
            value={verticalPosition}
            onChange={(e) => onVerticalPositionChange(parseInt(e.target.value))}
          />
          <span className="control-value">{verticalPosition}px</span>
        </div>
      </div>

      {/* Section Apparence (Couleur & Forme) */}
      <div className="param-box param-box-appearance">
        <h3 className="box-title">🎨 Apparence</h3>
        
        <div className="control-row">
          <div className="control-group compact inline">
            <label htmlFor="background-color">Fond</label>
            <input
              type="color"
              id="background-color"
              value={backgroundColor}
              onChange={(e) => onBackgroundColorChange(e.target.value)}
            />
          </div>
          
          <div className="control-group compact inline">
            <label htmlFor="ball-color">Objet</label>
            <input
              type="color"
              id="ball-color"
              value={ballColor}
              onChange={(e) => onBallColorChange(e.target.value)}
            />
          </div>
        </div>

        <div className="control-group compact">
          <label htmlFor="ball-shape">Forme</label>
          <div className="shape-selector compact">
            {shapes.map((shape) => (
              <button
                key={shape.value}
                className={`shape-option ${ballShape === shape.value ? 'selected' : ''}`}
                onClick={() => onBallShapeChange(shape.value)}
                title={shape.label}
              >
                {shape.value === 'circle' ? '⚪' : shape.value}
              </button>
            ))}
          </div>
        </div>

        <div className="control-group compact">
          <label htmlFor="ball-size">Taille</label>
          <input
            type="range"
            id="ball-size"
            min="0.5"
            max="3"
            step="0.1"
            value={ballSize}
            onChange={(e) => onBallSizeChange(parseFloat(e.target.value))}
          />
          <span className="control-value">{ballSize.toFixed(1)}x</span>
        </div>
      </div>

      {/* Section Son */}
      <div className="param-box param-box-audio">
        <h3 className="box-title">🔊 Son</h3>
        
        <div className="control-group compact">
          <label htmlFor="sound-type">Type</label>
          <select
            id="sound-type"
            value={soundType}
            onChange={(e) => onSoundTypeChange(e.target.value)}
          >
            <optgroup label="Sons synthétisés">
              <option value="soft-tone">Bip 1</option>
              <option value="ocean-wave">Bip 2</option>
              <option value="forest-sound">Bip 3</option>
              <option value="heartbeat">Bip 4</option>
            </optgroup>
            <optgroup label="Sons enregistrés 🎵">
              <option value="heartbeat-real">💓 Battement de cœur</option>
              <option value="tibetan-bowl">🎎 Bol tibétain</option>
              <option value="drum-1">🥁 Tambour 1</option>
              <option value="drum-2">🥁 Tambour 2</option>
              <option value="drum-3">🥁 Tambour 3</option>
              <option value="piano-1">🎹 Piano</option>
            </optgroup>
          </select>
        </div>

        <div className="control-row">
          <div className="control-group compact inline">
            <label htmlFor="volume">Volume</label>
            <input
              type="range"
              id="volume"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
            />
            <span className="control-value">{Math.round(volume * 100)}%</span>
          </div>
        </div>

        <div className="control-group compact">
          <label htmlFor="sound-frequency">Tonalité</label>
          <input
            type="range"
            id="sound-frequency"
            min="1"
            max="10"
            value={soundFrequency}
            onChange={(e) => onSoundFrequencyChange(parseInt(e.target.value))}
          />
          <span className="control-value">{soundFrequency}/10</span>
        </div>
      </div>

      {/* Section Vibration Haptique */}
      <div className="param-box param-box-haptic">
        <HapticControls
          joyConState={joyConState}
          onJoyConConnect={onJoyConConnect}
          onJoyConEnabledChange={onJoyConEnabledChange}
          onJoyConIntensityChange={onJoyConIntensityChange}
          gamepadState={gamepadState}
          onGamepadEnabledChange={onGamepadEnabledChange}
          onGamepadIntensityChange={onGamepadIntensityChange}
          onGamepadAssignSide={onGamepadAssignSide}
          onGamepadScan={onGamepadScan}
        />
      </div>

      {/* Raccourcis clavier - Section repliable */}
      <div className="collapsible-section">
        <div 
          className="section-header"
          onClick={() => toggleSection('shortcuts')}
        >
          <span>⌨️ Raccourcis clavier</span>
          <span className={`chevron ${expandedSections.shortcuts ? 'expanded' : ''}`}>▼</span>
        </div>
        {expandedSections.shortcuts && (
          <div className="section-content">
            <div className="shortcuts-list">
              {Object.entries(shortcuts || {}).map(([key, action]) => (
                <div key={key} className="shortcut-item">
                  <span className="shortcut-key">{key}</span>
                  <span className="shortcut-action">{action}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>

      {/* Page d'abonnement */}
      {showSubscriptionPage && (
        <SubscriptionPage onClose={() => setShowSubscriptionPage(false)} />
      )}
  </>
  )
}

export default ControlPanel 